import { createFileRoute } from '@tanstack/react-router';
import clsx from 'clsx';
import { type FC, useEffect } from 'react';
import { Button, Container, Nav, Navbar, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { AuthState } from '@furality/frontend-stack';
import DiscordAuthProvider, { useDiscordAuth } from '@/components/_safety/DiscordAuthProvider.tsx';
import styles from '@/components/fullscreen.module.scss';
import { NoLayout } from '@/components/layouts/index.tsx';

type DiscordTypeformProps = {
  formId: string;
};
const DiscordTypeform: FC<DiscordTypeformProps> = ({ formId }) => {
  const user = useDiscordAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (user.state === AuthState.NotLoggedIn) {
      user.login();
    }
  }, [user.state]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://embed.typeform.com/embed.js';
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  if (user.state === AuthState.Error) {
    return (
      <div className="w-100 d-flex justify-content-center align-items-center min-vh-100 flex-column">
        <p className="text-center text-danger">{t('shared.safety.discord_error')}</p>
        <button className="subtle-button text-underline" onClick={() => user.login()}>
          {t('shared.safety.retry')}
        </button>
      </div>
    );
  }

  if (user.state !== AuthState.LoggedIn) {
    return (
      <div className="w-100 d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t('shared.safety.loading_discord')}</span>
        </Spinner>
      </div>
    );
  }
  const url = `https://form.typeform.com/to/${formId}?${new URLSearchParams({
    'typeform-medium': 'embed-snippet',
    discord_id: user.user.id,
    discord_username: user.user.username,
  }).toString()}`;

  return (
    <div>
      <Navbar className="position-absolute w-100">
        <Container className="px-lg-0">
          <Nav className="align-items-center ml-auto">
            <Button
              onClick={() => user.logout()}
              variant="outline-secondary"
              className="unstyled-button"
              size="sm"
            >
              {t('logout')}
            </Button>
          </Nav>
        </Container>
      </Navbar>
      <iframe
        allow="encrypted-media"
        src={url}
        className={clsx('d-block', styles['fullscreen'])}
        title={`Typeform form for ${formId}`}
      />
    </div>
  );
};

export const Route = createFileRoute('/safety')({
  component: () => (
    <NoLayout title="shared.navigation.safety">
      <DiscordAuthProvider>
        <DiscordTypeform formId="w0VVmgep" />
      </DiscordAuthProvider>
    </NoLayout>
  ),
});
